// Weekly Digest admin page — manage which biotechs receive the per-asset
// Monday-9am-IST outreach digest, configure recipients/CC, preview the email,
// and trigger manual sends. The scheduler is wired in `server.py` startup;
// this UI is only the control surface.
import React, { useEffect, useState, useMemo } from 'react';
import { Mail, Search, Send, Eye, Power, Users, Calendar, X, CheckCircle2, AlertTriangle, ListChecks } from 'lucide-react';
import { toast } from 'sonner';

const API = '';

function authHeaders() {
  const token = localStorage.getItem('session_token') || '';
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function WeeklyDigestPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all | enabled | disabled | my
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const [scheduler, setScheduler] = useState(null);
  const [autoSend, setAutoSend] = useState(null);
  const [mondayBatch, setMondayBatch] = useState(null);
  const [showMondayBatch, setShowMondayBatch] = useState(false);
  const [building, setBuilding] = useState(false);

  useEffect(() => {
    loadAll();
    fetch(`${API}/api/digest/health`).then(r => r.json()).then(setScheduler).catch(() => {});
    loadSettings();
  }, []);

  async function loadSettings() {
    try {
      const r = await fetch(`${API}/api/digest/settings`, { headers: authHeaders(), credentials: 'include' });
      if (r.ok) setAutoSend(await r.json());
    } catch (e) { /* ignore */ }
  }

  async function toggleAutoSend(next) {
    if (next && !confirm('Enable AUTO-SEND? Real emails will go out next Monday 9 AM IST to every enabled biotech for every active asset. This is reversible.')) return;
    try {
      const r = await fetch(`${API}/api/digest/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        credentials: 'include',
        body: JSON.stringify({ auto_send_enabled: next }),
      });
      if (!r.ok) throw new Error('failed');
      toast.success(next ? 'Auto-send ENABLED' : 'Auto-send DISABLED');
      loadSettings();
    } catch (e) {
      toast.error(`Toggle failed: ${e.message}`);
    }
  }

  async function buildMondayBatch() {
    if (!confirm('Build a dry-run preview of every email that would go out next Monday? No emails will be sent.')) return;
    setBuilding(true);
    try {
      const r = await fetch(`${API}/api/digest/run-dry-batch`, {
        method: 'POST', headers: authHeaders(), credentials: 'include',
      });
      if (!r.ok) throw new Error('failed');
      const summary = await r.json();
      const list = await fetch(`${API}/api/digest/dry-run-log`, { headers: authHeaders(), credentials: 'include' });
      const ld = await list.json();
      setMondayBatch({ summary, rows: ld.rows || [] });
      setShowMondayBatch(true);
      toast.success(`Built ${summary.previewed} previews`);
    } catch (e) {
      toast.error(`Build failed: ${e.message}`);
    } finally {
      setBuilding(false);
    }
  }

  async function loadAll() {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/digest/biotechs`, { headers: authHeaders(), credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setRows(d.biotechs || []);
    } catch (e) {
      toast.error(`Failed to load: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (filter === 'enabled' && !r.enabled) return false;
      if (filter === 'disabled' && r.enabled) return false;
      if (filter === 'my' && !r.is_my_company) return false;
      if (q && !r.company_name.toLowerCase().includes(q)
          && !(r.primary_contact_email || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, search, filter]);

  const enabledCount = rows.filter(r => r.enabled).length;

  async function bulkEnable() {
    const ids = Array.from(selected);
    if (!ids.length) return toast.error('Select at least one biotech');
    if (!confirm(`Enable weekly digest for ${ids.length} biotech(s) using their primary contact email?`)) return;
    try {
      const r = await fetch(`${API}/api/digest/bulk-enable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        credentials: 'include',
        body: JSON.stringify({ biotech_ids: ids, use_primary_contact: true }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'failed');
      toast.success(`Enabled ${d.enabled} · Skipped ${d.skipped?.length || 0}`);
      setSelected(new Set());
      loadAll();
    } catch (e) {
      toast.error(`Bulk enable failed: ${e.message}`);
    }
  }

  async function toggle(row) {
    const action = row.enabled ? 'disable' : 'enable';
    if (action === 'enable') {
      // open editor instead of toggling directly so user confirms recipient
      setEditing(row);
      return;
    }
    try {
      const r = await fetch(`${API}/api/digest/biotechs/${row.id}/disable`, {
        method: 'POST', headers: authHeaders(), credentials: 'include',
      });
      if (!r.ok) throw new Error('failed');
      toast.success(`Disabled for ${row.company_name}`);
      loadAll();
    } catch (e) {
      toast.error(`Failed: ${e.message}`);
    }
  }

  async function preview(row) {
    setPreviewing({ row, loading: true });
    try {
      // pick the first active asset for this biotech
      const ar = await fetch(`${API}/api/assets?biotech_company_id=${row.id}&limit=1`, { headers: authHeaders(), credentials: 'include' });
      const ad = await ar.json();
      const asset = (ad.assets || ad)[0];
      if (!asset) throw new Error('No active asset for this biotech');
      const pr = await fetch(`${API}/api/digest/preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        credentials: 'include',
        body: JSON.stringify({ biotech_id: row.id, asset_id: asset.id }),
      });
      const pd = await pr.json();
      if (!pr.ok) throw new Error(pd.detail || 'preview failed');
      setPreviewing({ row, asset, data: pd, loading: false });
    } catch (e) {
      toast.error(`Preview failed: ${e.message}`);
      setPreviewing(null);
    }
  }

  async function sendNow(row) {
    if (!confirm(`Send weekly digest NOW for "${row.company_name}" to ${row.primary_contact_email}?`)) return;
    try {
      // need asset_id — use first active asset
      const ar = await fetch(`${API}/api/assets?biotech_company_id=${row.id}&limit=1`, { headers: authHeaders(), credentials: 'include' });
      const ad = await ar.json();
      const asset = (ad.assets || ad)[0];
      if (!asset) throw new Error('No active asset');
      const r = await fetch(`${API}/api/digest/send-now`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        credentials: 'include',
        body: JSON.stringify({ biotech_id: row.id, asset_id: asset.id, force: true }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'send failed');
      toast.success(`Sent to ${d.recipient}`);
      loadAll();
    } catch (e) {
      toast.error(`Send failed: ${e.message}`);
    }
  }

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="weekly-digest-page">
      <div className="flex items-start justify-between mb-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Mail className="w-6 h-6 text-violet-600" /> Weekly Outreach Digest
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            One personalized email per (biotech × active asset), every Monday 9 AM IST.
          </p>
        </div>
        {scheduler?.running && scheduler.jobs?.[0]?.next_run_time && (
          <div className="text-right">
            <div className="text-xs uppercase text-gray-500 font-semibold">Next scheduled run</div>
            <div className="text-sm text-gray-900 font-mono mt-1 flex items-center gap-1.5">
              <Calendar className="w-4 h-4 text-green-600" />
              {scheduler.jobs[0].next_run_time}
            </div>
          </div>
        )}
      </div>

      {/* Master safety banner — auto-send kill switch */}
      {autoSend && !autoSend.auto_send_enabled && (
        <div className="mt-4 bg-amber-50 border border-amber-300 rounded-lg p-4 flex items-start gap-3" data-testid="auto-send-off-banner">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-amber-900">Auto-send is OFF — no emails will go out automatically.</div>
            <div className="text-sm text-amber-800 mt-1">
              The Monday 9 AM IST scheduler is registered, but in dry-run mode it logs every intended send without actually emailing.
              Click <strong>Build Monday Preview</strong> below to see what would go out, review each one, then enable auto-send when ready.
            </div>
            <div className="mt-3 flex gap-2 flex-wrap">
              <button
                data-testid="build-monday-preview-btn"
                onClick={buildMondayBatch}
                disabled={building}
                className="inline-flex items-center gap-2 bg-amber-600 text-white px-3 py-1.5 rounded text-sm font-semibold hover:bg-amber-700 disabled:opacity-50"
              >
                <ListChecks className="w-4 h-4" />
                {building ? 'Building…' : 'Build Monday Preview'}
              </button>
              <button
                data-testid="enable-auto-send-btn"
                onClick={() => toggleAutoSend(true)}
                className="inline-flex items-center gap-2 bg-green-600 text-white px-3 py-1.5 rounded text-sm font-semibold hover:bg-green-700"
              >
                <Power className="w-4 h-4" /> Enable auto-send
              </button>
            </div>
          </div>
        </div>
      )}
      {autoSend && autoSend.auto_send_enabled && (
        <div className="mt-4 bg-green-50 border border-green-300 rounded-lg p-3 flex items-center gap-3" data-testid="auto-send-on-banner">
          <CheckCircle2 className="w-5 h-5 text-green-600" />
          <div className="flex-1 text-sm">
            <span className="font-semibold text-green-900">Auto-send is ON.</span>{' '}
            <span className="text-green-800">Next Monday 9 AM IST batch will deliver real emails.</span>
          </div>
          <button
            onClick={() => toggleAutoSend(false)}
            className="text-sm bg-white border border-red-300 text-red-700 px-3 py-1 rounded hover:bg-red-50"
          >
            Disable
          </button>
        </div>
      )}

      {/* Stats strip */}
      <div className="grid grid-cols-3 gap-3 mt-6 mb-5">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs uppercase text-gray-500 font-semibold">Biotechs total</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{rows.length}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs uppercase text-gray-500 font-semibold">Digest enabled</div>
          <div className="text-2xl font-bold text-green-600 mt-1">{enabledCount}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-xs uppercase text-gray-500 font-semibold">Selected</div>
          <div className="text-2xl font-bold text-violet-600 mt-1">{selected.size}</div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center gap-3 flex-wrap mb-3">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            data-testid="digest-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search biotech or contact email…"
            className="w-full border border-gray-300 rounded-lg py-2 pl-9 pr-3 text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
          />
        </div>
        {['all', 'my', 'enabled', 'disabled'].map(f => (
          <button
            key={f}
            data-testid={`digest-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 text-sm rounded-lg border ${
              filter === f
                ? 'bg-violet-600 text-white border-violet-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            {f === 'my' ? 'My companies' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        {selected.size > 0 && (
          <button
            data-testid="bulk-enable-btn"
            onClick={bulkEnable}
            className="ml-auto inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-700"
          >
            <Power className="w-4 h-4" /> Enable for {selected.size} selected
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading…</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-left text-xs uppercase text-gray-600 font-semibold">
                <th className="px-3 py-2.5 w-10">
                  <input
                    data-testid="select-all"
                    type="checkbox"
                    checked={filtered.length > 0 && filtered.every(r => selected.has(r.id))}
                    onChange={e => {
                      const next = new Set(selected);
                      if (e.target.checked) filtered.forEach(r => next.add(r.id));
                      else filtered.forEach(r => next.delete(r.id));
                      setSelected(next);
                    }}
                  />
                </th>
                <th className="px-3 py-2.5">Biotech</th>
                <th className="px-3 py-2.5">Recipient</th>
                <th className="px-3 py-2.5">CCs</th>
                <th className="px-3 py-2.5">Status</th>
                <th className="px-3 py-2.5">Last sent</th>
                <th className="px-3 py-2.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 200).map(r => (
                <tr key={r.id} data-testid={`digest-row-${r.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-3 py-2.5">
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={e => {
                        const next = new Set(selected);
                        if (e.target.checked) next.add(r.id);
                        else next.delete(r.id);
                        setSelected(next);
                      }}
                    />
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-gray-900 flex items-center gap-2">
                      {r.company_name}
                      {r.is_my_company && (
                        <span className="text-[10px] bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded font-semibold">MINE</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-gray-600">
                    <div className="text-xs">{r.primary_contact_name || '—'}</div>
                    <div className="text-[11px] text-gray-400">{r.primary_contact_email || 'no email'}</div>
                  </td>
                  <td className="px-3 py-2.5 text-gray-600 text-xs">
                    {r.cc_emails?.length ? `${r.cc_emails.length} CC` : '—'}
                  </td>
                  <td className="px-3 py-2.5">
                    {r.enabled ? (
                      <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded font-semibold">
                        <CheckCircle2 className="w-3 h-3" /> Enabled
                      </span>
                    ) : (
                      <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">Off</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-gray-500">
                    {r.last_sent_at ? new Date(r.last_sent_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="inline-flex gap-1.5">
                      <button
                        data-testid={`preview-${r.id}`}
                        onClick={() => preview(r)}
                        className="p-1.5 text-gray-600 hover:text-violet-600 hover:bg-violet-50 rounded"
                        title="Preview email"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`config-${r.id}`}
                        onClick={() => setEditing(r)}
                        className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                        title="Configure recipient + CCs"
                      >
                        <Users className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`toggle-${r.id}`}
                        onClick={() => toggle(r)}
                        className={`p-1.5 rounded ${
                          r.enabled
                            ? 'text-red-600 hover:bg-red-50'
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                        title={r.enabled ? 'Disable' : 'Enable'}
                      >
                        <Power className="w-4 h-4" />
                      </button>
                      {r.enabled && (
                        <button
                          data-testid={`send-${r.id}`}
                          onClick={() => sendNow(r)}
                          className="p-1.5 text-violet-600 hover:bg-violet-50 rounded"
                          title="Send now"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length > 200 && (
            <div className="px-3 py-2 text-xs text-gray-500 text-center bg-gray-50">
              Showing first 200 of {filtered.length}. Refine your search.
            </div>
          )}
        </div>
      )}

      {/* Config Modal */}
      {editing && (
        <ConfigModal
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadAll(); }}
        />
      )}

      {/* Preview Modal */}
      {previewing && (
        <PreviewModal data={previewing} onClose={() => setPreviewing(null)} />
      )}

      {/* Monday batch preview modal */}
      {showMondayBatch && mondayBatch && (
        <MondayBatchModal
          batch={mondayBatch}
          onClose={() => setShowMondayBatch(false)}
          onApprove={() => { toggleAutoSend(true); setShowMondayBatch(false); }}
        />
      )}
    </div>
  );
}

function MondayBatchModal({ batch, onClose, onApprove }) {
  const [selected, setSelected] = useState(0);
  const rows = batch.rows || [];
  const current = rows[selected];
  const [html, setHtml] = useState(null);

  useEffect(() => {
    if (!current) return setHtml(null);
    setHtml(null);
    fetch(`${API}/api/digest/preview`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      credentials: 'include',
      body: JSON.stringify({ biotech_id: current.biotech_id, asset_id: current.asset_id }),
    }).then(r => r.json()).then(d => setHtml(d.html || '')).catch(() => setHtml(''));
  }, [selected, current]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[92vh] flex flex-col" data-testid="monday-batch-modal">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="font-semibold text-gray-900">
              Monday Batch Preview · {rows.length} email{rows.length === 1 ? '' : 's'} queued
            </h3>
            <div className="text-xs text-gray-500 mt-0.5">
              Review each one. Nothing has been sent. Click "Approve & Enable Auto-Send" once everything looks right.
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left: list of pending */}
          <div className="w-80 border-r border-gray-200 overflow-y-auto bg-gray-50">
            {rows.length === 0 && (
              <div className="p-6 text-sm text-gray-500 italic">
                Nothing queued. Enable digest for at least one biotech first, then re-run the batch.
              </div>
            )}
            {rows.map((r, i) => (
              <button
                key={`${r.biotech_id}-${r.asset_id}`}
                data-testid={`monday-row-${i}`}
                onClick={() => setSelected(i)}
                className={`w-full text-left p-3 border-b border-gray-200 hover:bg-white ${
                  selected === i ? 'bg-white border-l-4 border-l-violet-600' : ''
                }`}
              >
                <div className="font-medium text-sm text-gray-900 truncate">{r.biotech_name}</div>
                <div className="text-xs text-gray-600 truncate mt-0.5">{r.asset_name}</div>
                <div className="text-[11px] text-gray-400 truncate mt-1">→ {r.recipient}</div>
                {r.cc && <div className="text-[11px] text-gray-400 truncate">cc: {r.cc}</div>}
              </button>
            ))}
          </div>

          {/* Right: rendered HTML preview */}
          <div className="flex-1 overflow-y-auto bg-gray-100 p-3">
            {current ? (
              <>
                <div className="bg-white border border-gray-200 rounded p-3 mb-3 text-xs">
                  <div><strong>Subject:</strong> {current.subject}</div>
                  <div><strong>To:</strong> {current.recipient}</div>
                  {current.cc && <div><strong>Cc:</strong> {current.cc}</div>}
                </div>
                {html === null ? (
                  <div className="text-center py-12 text-gray-400">Loading preview…</div>
                ) : html ? (
                  <iframe
                    title="monday-preview"
                    srcDoc={html}
                    className="w-full bg-white border border-gray-200 rounded"
                    style={{ minHeight: '60vh' }}
                  />
                ) : (
                  <div className="p-6 text-sm text-red-600">Could not generate HTML for this row.</div>
                )}
              </>
            ) : (
              <div className="p-6 text-sm text-gray-500">Pick a row on the left to preview.</div>
            )}
          </div>
        </div>

        <div className="p-4 border-t border-gray-200 flex justify-between items-center bg-gray-50">
          <div className="text-xs text-gray-600">
            Reviewing {selected + 1} of {rows.length}
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-white">
              Close
            </button>
            <button
              data-testid="approve-batch-btn"
              onClick={onApprove}
              disabled={rows.length === 0}
              className="px-4 py-1.5 text-sm bg-green-600 text-white rounded font-semibold hover:bg-green-700 disabled:opacity-50"
            >
              Approve & Enable Auto-Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfigModal({ row, onClose, onSaved }) {
  const [primary, setPrimary] = useState(row.primary_contact_email || '');
  const [primaryName, setPrimaryName] = useState(row.primary_contact_name || '');
  const [ccs, setCcs] = useState(row.cc_emails || []);
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!primary || !primary.includes('@')) {
      toast.error('Valid primary email required');
      return;
    }
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/digest/biotechs/${row.id}/enable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        credentials: 'include',
        body: JSON.stringify({
          primary_contact_email: primary.trim(),
          primary_contact_name: primaryName.trim(),
          cc_emails: ccs.filter(e => e && e.includes('@')),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'save failed');
      toast.success(`Saved · digest enabled for ${row.company_name}`);
      onSaved();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" data-testid="digest-config-modal">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-900">Configure digest · {row.company_name}</h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold text-gray-700 uppercase">Primary recipient</label>
            <select
              data-testid="primary-contact-select"
              value={primary}
              onChange={e => {
                const c = (row.available_contacts || []).find(x => x.email === e.target.value);
                setPrimary(e.target.value);
                if (c) setPrimaryName(c.name || '');
              }}
              className="mt-1 w-full border border-gray-300 rounded-lg p-2 text-sm"
            >
              <option value="">— Select contact —</option>
              {(row.available_contacts || []).map(c => (
                <option key={c.email} value={c.email}>
                  {c.name} {c.designation ? `· ${c.designation}` : ''} · {c.email}
                </option>
              ))}
            </select>
            <input
              type="email"
              placeholder="…or type a custom email"
              value={primary}
              onChange={e => setPrimary(e.target.value)}
              className="mt-2 w-full border border-gray-300 rounded-lg p-2 text-sm"
            />
            <input
              type="text"
              placeholder="Recipient name (for greeting)"
              value={primaryName}
              onChange={e => setPrimaryName(e.target.value)}
              className="mt-2 w-full border border-gray-300 rounded-lg p-2 text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-700 uppercase">CC contacts</label>
            <p className="text-xs text-gray-500 mt-1">Add additional people from the biotech who should be copied.</p>
            <div className="mt-2 space-y-1.5">
              {(row.available_contacts || []).filter(c => c.email !== primary).map(c => (
                <label key={c.email} className="flex items-center gap-2 text-sm py-1">
                  <input
                    type="checkbox"
                    checked={ccs.includes(c.email)}
                    onChange={e => {
                      if (e.target.checked) setCcs([...ccs, c.email]);
                      else setCcs(ccs.filter(x => x !== c.email));
                    }}
                  />
                  <span className="text-gray-700">{c.name}</span>
                  <span className="text-gray-400 text-xs">{c.designation || ''}</span>
                  <span className="text-gray-400 text-xs ml-auto">{c.email}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-gray-200 flex justify-end gap-2 bg-gray-50">
          <button onClick={onClose} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-white">
            Cancel
          </button>
          <button
            data-testid="save-config-btn"
            onClick={save}
            disabled={saving || !primary}
            className="px-4 py-1.5 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save & Enable'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PreviewModal({ data, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col" data-testid="digest-preview-modal">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div>
            <h3 className="font-semibold text-gray-900">Preview · {data.row.company_name}</h3>
            {data.data && <div className="text-xs text-gray-500 mt-0.5">To: {data.data.recipient} · Subject: {data.data.subject}</div>}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="overflow-y-auto p-2 bg-gray-100">
          {data.loading ? (
            <div className="text-center py-12 text-gray-400">Generating preview…</div>
          ) : data.data?.html ? (
            <iframe
              title="digest-preview"
              srcDoc={data.data.html}
              className="w-full bg-white border border-gray-200 rounded"
              style={{ minHeight: '60vh' }}
            />
          ) : (
            <div className="p-6 text-sm text-red-600">Could not load preview</div>
          )}
        </div>
      </div>
    </div>
  );
}
